import React, { useState, useRef } from 'react';
import axios from '../../utils/axios';
import { Upload, X, Image as ImageIcon, Loader2, GripVertical, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getPublicAllergenIconPath, isAllergenIconImage } from '../../utils/publicAllergens';
import { getAllergenDisplayNameItalian } from '../../utils/allergenDisplayNames';
import { ConfirmDialog } from '../common/ConfirmDialog';

const getAllergenIconSrc = (allergen) =>
  getPublicAllergenIconPath(allergen?.code) || (allergen?.icon && isAllergenIconImage(allergen.icon) ? allergen.icon : null);
const getAllergenDisplayName = (allergen) => getAllergenDisplayNameItalian({ id: allergen?.id, name: allergen?.name, code: allergen?.code, icon: allergen?.icon });

const DRAG_TYPE_DISH = 'dish';

const DishManager = ({ categoryId, dishes, allergens, onUpdate, selectedLanguage, onMoveDishToCategory }) => {
  const { t } = useTranslation();
  const [dragOverDishId, setDragOverDishId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDish, setNewDish] = useState({
    title: '',
    description: '',
    price: '',
    calories: '',
    imageUrl: '',
    allergens: []
  });
  const [editingDish, setEditingDish] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingImageId, setUploadingImageId] = useState(null);
  const [selectedDishIds, setSelectedDishIds] = useState([]);
  // Confirm dialog state for dish deletes
  const [confirmDishDialog, setConfirmDishDialog] = useState({
    isOpen: false,
    dishId: null,
    isBulk: false,
    title: '',
    message: ''
  });
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const handleAddDish = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/menu/management/categories/${categoryId}/items`, {
        name: newDish.title,
        description: newDish.description,
        price: newDish.price,
        calories: newDish.calories ? parseInt(newDish.calories) : null,
        imageUrl: newDish.imageUrl || '',
        allergens: newDish.allergens
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setNewDish({ title: '', description: '', price: '', calories: '', imageUrl: '', allergens: [] });
        setShowAddForm(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        onUpdate();
        toast.success(t('dish_manager.messages.dish_added_success'));
      }
    } catch (error) {
      console.error('Failed to add dish:', error);
      toast.error(error.response?.data?.error || t('dish_manager.messages.dish_add_error'));
    }
  };

  const handleUpdateDish = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/menu/management/items/${editingDish.id}`, {
        name: editingDish.name,
        description: editingDish.description,
        price: editingDish.price,
        calories: editingDish.calories ? parseInt(editingDish.calories) : null,
        imageUrl: editingDish.imageUrl,
        allergens: editingDish.allergens || []
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setEditingDish(null);
        onUpdate();
        toast.success(t('dish_manager.messages.dish_updated_success'));
      }
    } catch (error) {
      console.error('Failed to update dish:', error);
      toast.error(error.response?.data?.error || t('dish_manager.messages.dish_update_error'));
    }
  };

  // Open confirm dialog for single dish delete
  const handleDeleteDish = (dishId) => {
    const dish = (dishes || []).find(d => d.id === dishId);
    setConfirmDishDialog({
      isOpen: true,
      dishId,
      isBulk: false,
      title: t('dish_manager.messages.confirm_delete_title') || 'Delete Dish',
      message: dish
        ? `Are you sure you want to delete "${dish.name}"? This cannot be undone.`
        : (t('dish_manager.messages.confirm_delete') || 'Are you sure you want to delete this dish?')
    });
  };

  // Execute single dish delete (optimistic: remove from local list)
  const executeDeleteDish = async (dishId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/menu/management/items/${dishId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // Optimistic: remove from local dishes via onUpdate — but we
        // can't modify dishes prop directly, so call onUpdate (the
        // parent already does a silent re-fetch via fetchMenuData(true))
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to delete dish:', error);
      toast.error(error.response?.data?.error || 'Failed to delete dish');
    }
  };

  const handleBulkDelete = () => {
    if (selectedDishIds.length === 0) return;
    const count = selectedDishIds.length;
    setConfirmDishDialog({
      isOpen: true,
      dishId: null,
      isBulk: true,
      title: `Delete ${count} ${count === 1 ? 'Dish' : 'Dishes'}`,
      message: `Are you sure you want to delete ${count} selected ${count === 1 ? 'dish' : 'dishes'}? This cannot be undone.`
    });
  };

  const executeBulkDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/menu/management/items/bulk-delete`, 
        { itemIds: selectedDishIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(t('dish_manager.messages.bulk_delete_success') || 'Dishes deleted successfully');
        setSelectedDishIds([]);
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to bulk delete dishes:', error);
      toast.error(error.response?.data?.error || 'Failed to delete dishes');
    }
  };

  const toggleDishSelection = (dishId) => {
    setSelectedDishIds(prev => 
      prev.includes(dishId) 
        ? prev.filter(id => id !== dishId)
        : [...prev, dishId]
    );
  };

  const toggleAllSelection = () => {
    if (selectedDishIds.length === (dishes || []).length) {
      setSelectedDishIds([]);
    } else {
      setSelectedDishIds((dishes || []).map(d => d.id));
    }
  };

  const getDishDragData = (dish) => JSON.stringify({ type: DRAG_TYPE_DISH, id: dish.id, categoryId: Number(categoryId) });

  const handleDishDragStart = (e, dish) => {
    e.stopPropagation();
    const data = getDishDragData(dish);
    e.dataTransfer.setData('text/plain', data);
    e.dataTransfer.setData('application/json', data);
    e.dataTransfer.effectAllowed = 'move';
    const row = e.currentTarget.closest('[data-dish-row]');
    if (row) row.classList.add('opacity-50');
  };
  const handleDishDragEnd = (e) => {
    const row = e.currentTarget.closest('[data-dish-row]');
    if (row) row.classList.remove('opacity-50');
    setDragOverDishId(null);
  };
  const handleDishDragOver = (e, targetDishId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDishId(targetDishId);
  };
  const parseDishDropData = (e) => {
    const raw = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('application/json');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  };
  const handleDishDrop = async (e, targetDishId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDishId(null);
    const payload = parseDishDropData(e);
    if (!payload || payload.type !== DRAG_TYPE_DISH) return;
    const { id: sourceId, categoryId: sourceCategoryId } = payload;
    const sourceCat = Number(sourceCategoryId);
    const targetCat = Number(categoryId);
    // Drop from another category: move dish to this category
    if (sourceCat !== targetCat) {
      if (onMoveDishToCategory && sourceId) {
        await onMoveDishToCategory(Number(sourceId), categoryId);
      }
      return;
    }
    // Same category: reorder
    if (Number(sourceId) === Number(targetDishId)) return;
    const list = [...(dishes || [])];
    const fromIdx = list.findIndex(d => d.id === sourceId);
    const toIdx = list.findIndex(d => d.id === targetDishId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [removed] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, removed);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/menu/management/categories/${categoryId}/items/positions`, {
        items: list.map((d, i) => ({ id: d.id, position: i }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('dish_manager.messages.dish_updated_success'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to reorder';
      toast.error(msg);
    }
  };

  const reorderDishes = async (newList) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/menu/management/categories/${categoryId}/items/positions`, {
        items: newList.map((d, i) => ({ id: d.id, position: i }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('dish_manager.messages.dish_updated_success'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to reorder';
      toast.error(msg);
    }
  };

  const moveDishUp = async (index) => {
    if (index <= 0) return;
    const list = [...(dishes || [])];
    [list[index - 1], list[index]] = [list[index], list[index - 1]];
    await reorderDishes(list);
  };

  const moveDishDown = async (index) => {
    const list = [...(dishes || [])];
    if (index >= list.length - 1) return;
    [list[index], list[index + 1]] = [list[index + 1], list[index]];
    await reorderDishes(list);
  };

  const toggleAllergen = (allergenId) => {
    setNewDish(prev => ({
      ...prev,
      allergens: prev.allergens.includes(allergenId)
        ? prev.allergens.filter(id => id !== allergenId)
        : [...prev.allergens, allergenId]
    }));
  };

  const handleImageUpload = async (file, itemId = null) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('dish_manager.messages.please_select_image'));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('dish_manager.messages.image_size_limit'));
      return;
    }

    setUploadingImage(true);
    if (itemId) setUploadingImageId(itemId);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);

      if (itemId) {
        // For existing dish, upload and update
        const response = await axios.post(`/menu/management/items/${itemId}/image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          toast.success(t('dish_manager.messages.image_uploaded_success'));
          onUpdate();
        }
      } else {
        // For new dish, upload first to get URL, then store in state
        // Create a temporary item to upload image, or use a different approach
        // For now, we'll upload to a temp endpoint or convert to base64
        // Better approach: Upload image first, get URL, then use it when creating dish
        const response = await axios.post(`/menu/management/items/temp-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          const imageUrl = response.data.data.imageUrl;
          setNewDish(prev => ({ ...prev, imageUrl }));
          toast.success(t('dish_manager.messages.image_ready'));
        }
      }
    } catch (error) {
      // If temp endpoint doesn't exist, use base64 for preview
      if (!itemId && error.response?.status === 404) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setNewDish(prev => ({ ...prev, imageUrl: reader.result}));
          toast.success(t('dish_manager.messages.image_ready_note'));
        };
        reader.readAsDataURL(file);
      } else {
        console.error('Image upload error:', error);
        toast.error(error.response?.data?.error || t('dish_manager.messages.upload_error'));
      }
    } finally {
      setUploadingImage(false);
      setUploadingImageId(null);
    }
  };

  const handleImageChange = (e, itemId = null) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, itemId);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <h4 className="font-medium text-gray-900">{t('dish_manager.title')}</h4>
          {selectedDishIds.length > 0 && (
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {selectedDishIds.length} selected
            </span>
          )}
        </div>
        
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          {selectedDishIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="text-sm bg-red-100 hover:bg-red-200 text-red-600 px-3 py-1.5 rounded flex items-center"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected
            </button>
          )}
          <button
            onClick={() => setShowAddForm(!showAddForm)} 
            className="text-sm bg-[#7BD5B5] hover:bg-[#6bc4a5] text-[#0A0C0B] px-3 py-1.5 rounded flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {showAddForm ? t('dish_manager.buttons.cancel') : t('dish_manager.buttons.add_dish')}
          </button>
        </div>
      </div>

      {/* Add Dish Form */}
      {showAddForm && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <h5 className="font-medium text-gray-900 mb-3">{t('dish_manager.forms.add_new_dish')}</h5>
          <form onSubmit={handleAddDish} className="space-y-4">
            <div>
              <input
                type="text"
                value={newDish.title}
                onChange={(e) => setNewDish({...newDish, title: e.target.value})}
                placeholder={t('dish_manager.forms.dish_title_placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <textarea
              value={newDish.description}
              onChange={(e) => setNewDish({...newDish, description: e.target.value})}
              placeholder={t('dish_manager.forms.description_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dish_manager.forms.price')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newDish.price}
                  onChange={(e) => setNewDish({...newDish, price: e.target.value})}
                  placeholder={t('dish_manager.forms.price_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dish_manager.forms.calories')}</label>
                <input
                  type="number"
                  min="0"
                  value={newDish.calories}
                  onChange={(e) => setNewDish({...newDish, calories: e.target.value})}
                  placeholder={t('dish_manager.forms.calories_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dish_manager.forms.dish_image')}
              </label>
              <div className="flex items-center gap-4">
                {newDish.imageUrl ? (
                  <div className="relative">
                    <img
                      src={newDish.imageUrl}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setNewDish(prev => ({ ...prev, imageUrl: '' }));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e)}
                    className="hidden"
                    id="dish-image-upload"
                  />
                  <label
                    htmlFor="dish-image-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer text-sm transition-colors"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('dish_manager.messages.upload_success')}
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        {t('dish_manager.buttons.upload_image')}
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">{t('dish_manager.messages.image_size_info')}</p>
                </div>
              </div>
            </div>
            
            {/* Allergens Selection */}
            {allergens.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dish_manager.forms.allergens')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {allergens.map(allergen => (
                    <button
                      key={allergen.id}
                      type="button"
                      onClick={() => toggleAllergen(allergen.id)}
                      className={`px-3 py-1 text-sm rounded-full border ${
                        newDish.allergens.includes(allergen.id)
                          ? 'bg-red-100 text-red-800 border-red-300'
                          : 'bg-gray-100 text-gray-800 border-gray-300'
                      }`}
                    >
                      {getAllergenIconSrc(allergen) ? (
                        isAllergenIconImage(getAllergenIconSrc(allergen)) ? (
                          <img src={getAllergenIconSrc(allergen)} alt={getAllergenDisplayName(allergen)} className="w-4 h-4 inline-block align-middle mr-1" />
                        ) : (
                          <span className="mr-1">{allergen.icon}</span>
                        )
                      ) : null}
                      {getAllergenDisplayName(allergen)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-2 space-y-2 sm:space-y-0">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewDish({ title: '', description: '', price: '', calories: '', imageUrl: '', allergens: [] });
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('dish_manager.buttons.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('dish_manager.buttons.add_dish')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Dishes List */}
      <div className="space-y-3">
        {dishes && dishes.length > 0 && (
          <div className="flex items-center px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectedDishIds.length === dishes.length}
              onChange={toggleAllSelection}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="selectAll" className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
              {t('dish_manager.buttons.select_all') || 'Select All'}
            </label>
          </div>
        )}
        {dishes && dishes.length > 0 ? (
          dishes.map((dish, dishIndex) => (
            <div
              key={dish.id}
              data-dish-row
              className={`border rounded-lg p-4 bg-white shadow-sm transition-colors ${dragOverDishId === dish.id ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200'}`}
              onDragOver={(e) => handleDishDragOver(e, dish.id)}
              onDragLeave={() => setDragOverDishId(null)}
              onDrop={(e) => handleDishDrop(e, dish.id)}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={selectedDishIds.includes(dish.id)}
                    onChange={() => toggleDishSelection(dish.id)}
                    className="w-4 h-4 mr-2 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <span
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(e) => handleDishDragStart(e, dish)}
                    onDragEnd={handleDishDragEnd}
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-blue-600 sm:mt-1 touch-none p-0.5"
                    title={t('dish_manager.buttons.drag_to_reorder') || 'Trascina per riordinare'}
                  >
                    <GripVertical className="w-4 h-4" />
                  </span>
                  <button
                    type="button"
                    onClick={() => moveDishUp(dishIndex)}
                    disabled={dishIndex === 0}
                    className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('dish_manager.buttons.move_up') || 'Sposta su'}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDishDown(dishIndex)}
                    disabled={dishIndex === dishes.length - 1}
                    className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('dish_manager.buttons.move_down') || 'Sposta giù'}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                {/* Dish Image */}
                {dish.imageUrl && (
                  <div className="flex-shrink-0">
                    <img
                      src={dish.imageUrl}
                      alt={dish.name}
                      className="w-20 h-20 object-cover rounded-lg border border-gray-300"
                      draggable={false}
                    />
                  </div>
                )}
                        
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-gray-900 truncate">{dish.name}</h5>
                      {dish.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{dish.description}</p>
                      )}
                              
                      {/* Display Allergens */}
                      {dish.allergens && dish.allergens.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {dish.allergens.map(allergen => {
                            const iconSrc = getAllergenIconSrc(allergen);
                            const name = getAllergenDisplayName(allergen);
                            return (
                              <span key={allergen.id} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded truncate inline-flex items-center gap-1">
                                {iconSrc && isAllergenIconImage(iconSrc) ? (
                                  <img src={iconSrc} alt={name} className="w-3.5 h-3.5 object-contain flex-shrink-0" />
                                ) : iconSrc ? (
                                  <span>{allergen.icon}</span>
                                ) : null}
                                {name}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                            
                    <div className="flex flex-col items-start sm:items-end space-y-2">
                      {/* Price and Calories on same line */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">
                          €{parseFloat(dish.price).toFixed(2)}
                        </span>
                        {dish.calories && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded whitespace-nowrap">
                            {dish.calories} kcal
                          </span>
                        )}
                      </div>
                                          
                      {/* Action buttons on next line */}
                      <div className="flex flex-wrap gap-2">
                        {/* Image Upload Button */}
                        <input
                          ref={editFileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageChange(e, dish.id)}
                          className="hidden"
                          id={`dish-image-edit-${dish.id}`}
                        />
                        <label
                          htmlFor={`dish-image-edit-${dish.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 cursor-pointer whitespace-nowrap transition-colors"
                          title={t('dish_manager.tooltips.upload_change_image')}
                        >
                          {uploadingImageId === dish.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <ImageIcon className="w-3 h-3" />
                          )}
                          {uploadingImageId === dish.id ? t('dish_manager.messages.upload_success') : t('dish_manager.buttons.upload_image')}
                        </label>
                                            
                        <button
                          onClick={() => setEditingDish({
                            ...dish,
                            calories: dish.calories || ''
                          })}
                          className="p-2 text-gray-600 hover:text-blue-600 rounded-md transition-colors"
                          title={t('dish_manager.tooltips.edit_dish')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteDish(dish.id)}
                          className="p-2 text-gray-600 hover:text-red-600 rounded-md transition-colors"
                          title={t('dish_manager.tooltips.delete_dish')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center py-4 text-sm">{t('dish_manager.empty_state.no_dishes')}</p>
        )}
      </div>

      {/* Edit Dish Modal */}
      {editingDish && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dish_manager.forms.edit_dish')}</h3>
            <form onSubmit={handleUpdateDish}>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dish_manager.labels.dish_name')}</label>
                <input
                  type="text"
                  value={editingDish.name}
                  onChange={(e) => setEditingDish({...editingDish, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dish_manager.forms.description')}</label>
                <textarea
                  value={editingDish.description || ''}
                  onChange={(e) => setEditingDish({...editingDish, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder={t('dish_manager.forms.description_placeholder')}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('dish_manager.forms.price')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingDish.price}
                    onChange={(e) => setEditingDish({...editingDish, price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('dish_manager.forms.price_placeholder')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('dish_manager.forms.calories')}</label>
                  <input
                    type="number"
                    min="0"
                    value={editingDish.calories || ''}
                    onChange={(e) => setEditingDish({...editingDish, calories: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('dish_manager.forms.calories_placeholder')}
                  />
                </div>
              </div>
              
              {/* Allergens Selection in Edit Form */}
              {allergens.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dish_manager.forms.allergens')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {allergens.map(allergen => (
                      <button
                        key={allergen.id}
                        type="button"
                        onClick={() => {
                          setEditingDish(prev => {
                            const currentAllergens = prev.allergens || [];
                            const isSelected = currentAllergens.includes(allergen.id);
                            return {
                              ...prev,
                              allergens: isSelected
                                ? currentAllergens.filter(id => id !== allergen.id)
                                : [...currentAllergens, allergen.id]
                            };
                          });
                        }}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          (editingDish.allergens || []).includes(allergen.id)
                            ? 'bg-red-100 text-red-800 border-red-300'
                            : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        {getAllergenIconSrc(allergen) && isAllergenIconImage(getAllergenIconSrc(allergen)) ? (
                          <img src={getAllergenIconSrc(allergen)} alt={getAllergenDisplayName(allergen)} className="w-4 h-4 inline-block align-middle mr-1" />
                        ) : allergen.icon ? (
                          <span className="mr-1">{allergen.icon}</span>
                        ) : null}
                        {getAllergenDisplayName(allergen)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Image in Edit Form */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dish_manager.forms.dish_image')}
                </label>
                <div className="flex items-center gap-4">
                  {editingDish.imageUrl ? (
                    <img
                      src={editingDish.imageUrl}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                    />
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file, editingDish.id);
                      }}
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('dish_manager.messages.image_size_info')}</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-2 space-y-2 sm:space-y-0">
                <button
                  type="button"
                  onClick={() => {
                    setEditingDish(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {t('dish_manager.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
                >
                  {t('dish_manager.buttons.save_changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm dialog for dish delete */}
      <ConfirmDialog
        isOpen={confirmDishDialog.isOpen}
        onClose={() => setConfirmDishDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={() => {
          if (confirmDishDialog.isBulk) {
            executeBulkDelete();
          } else {
            executeDeleteDish(confirmDishDialog.dishId);
          }
        }}
        title={confirmDishDialog.title}
        message={confirmDishDialog.message}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default DishManager;